package com.oasisfeng.invaluable.event

import com.oasisfeng.invaluable.Link
import com.oasisfeng.invaluable.data.LinkProjection
import com.oasisfeng.invaluable.data.LinkProjector
import com.oasisfeng.invaluable.data.MemoizingLinkRecordResolver
import com.oasisfeng.invaluable.data.ProjectedLinkRecord
import com.oasisfeng.invaluable.data.ProjectedLinkRecordResolver
import com.oasisfeng.invaluable.data.memoizingOver
import com.oasisfeng.invaluable.identity.Codec
import kotlinx.io.bytestring.ByteString

/** Validate a whole batch against [base] without mutating it. */
fun validateReplicatedEventBatch(batch: ReplicatedEventBatch, base: ReplicatedEventStore): BatchValidationResult =
    validateReplicatedEventBatch(batch, StoreValidationBase(base))

/** Minimal read surface needed to validate a batch against a compact archive. */
interface ReplicatedEventBatchValidationBase {
    val projector: LinkProjector
    fun resolver(): ProjectedLinkRecordResolver
    fun containsAttestation(head: LinkProjection): Boolean
    fun containsSealed(contentProjection: LinkProjection, ciphertextDigest: ByteString): Boolean
}

/** Validate without requiring the compact archive to masquerade as a per-event mutable store. */
fun validateReplicatedEventBatch(
    batch: ReplicatedEventBatch,
    base: ReplicatedEventBatchValidationBase,
): BatchValidationResult {
    val overlay = ValidationOverlayStore(base)
    val applier = ReplicatedEventApplier(overlay)
    // Preimages first makes payload line order irrelevant while still rejecting dependencies absent from both the batch
    // and the durable base. Seals are opaque carriers and can be checked last.
    val ordered = batch.events.sortedBy { when (it) {
        is LinkPreimage -> 0
        is SignedCommitment -> 1
        is SealedContent -> 2
    }}
    for (event in ordered) when (val result = applier.apply(event)) {
        is ReplicatedEventApplier.ApplyResult.Applied, is ReplicatedEventApplier.ApplyResult.Duplicate -> Unit
        is ReplicatedEventApplier.ApplyResult.Invalid -> return BatchValidationResult.Invalid(result.reason)
        is ReplicatedEventApplier.ApplyResult.Pending ->
            return BatchValidationResult.Invalid(
                "Batch has unresolved dependencies: ${result.missing.size}; first=" +
                    Codec.base64Url(result.missing.first().payload.toByteArray()),
            )
    }
    return BatchValidationResult.Valid(accepted = overlay.accepted(), resolutions = overlay.resolutions())
}

/** Result of validating one atomic batch against either an expanded store or compact archive. */
sealed interface BatchValidationResult {
    /**
     * The batch is acceptable whole, and [accepted] is exactly what it adds — the validation's product, not a by-product.
     *
     * Judging an event and admitting it are two different acts, and only the first is expensive: it resolves the issuer
     * credential, verifies an Ed25519 signature, and attributes the head. A caller that re-ran [ReplicatedEventApplier]
     * over the same events to admit them paid for that judgement twice. Committing [accepted] through the store's own
     * `append` — pure insertion — is the other half, and it is the caller's job because only the real store maintains
     * the indexes (see [ReplicatedEventNode.commitValidated]).
     */
    data class Valid(
        val accepted: List<ReplicatedEvent>,
        /**
         * Links this validation resolved along the way, for the committer to adopt once [accepted] is durable.
         *
         * Validating an attestation rebuilds its head's Link, and a commitment's Link is its whole ancestry — so this is
         * most of the work in the chain below it. The validation pass reads the store's memo but cannot write back
         * (its records are still only proposed), so without handing these over the *next* batch would rebuild the same
         * ancestry from scratch: linear per batch, quadratic over a log. See [MemoizingLinkRecordResolver.adopt].
         */
        val resolutions: Map<LinkProjection, Link> = emptyMap(),
    ) : BatchValidationResult {
        /** How many distinct events the batch actually adds. */
        val uniqueEvents: Int get() = accepted.size
    }
    data class Invalid(val reason: String) : BatchValidationResult
}

/** Copy-on-write store used only for all-or-nothing validation. */
private class StoreValidationBase(private val store: ReplicatedEventStore) : ReplicatedEventBatchValidationBase {
    override val projector get() = store.projector
    override fun resolver(): ProjectedLinkRecordResolver = store.resolver()
    override fun containsAttestation(head: LinkProjection): Boolean = store.containsAttestation(head)
    override fun containsSealed(contentProjection: LinkProjection, ciphertextDigest: ByteString): Boolean =
        store.sealedFor(contentProjection).any { it.ciphertextDigest == ciphertextDigest }
}

private class ValidationOverlayStore(private val base: ReplicatedEventBatchValidationBase) : ReplicatedEventStore {
    override val projector get() = base.projector
    val addedPreimages = linkedMapOf<LinkProjection, ProjectedLinkRecord>()
    val addedAttestations = linkedMapOf<LinkProjection, SignedCommitment>()
    val addedSeals = linkedMapOf<Pair<ByteString, LinkProjection>, SealedContent>()

    private val baseResolver = base.resolver()

    /**
     * One memo for the whole batch, opened over the base store's own — rather than a fresh resolver per call.
     *
     * Two things were wrong before. [ReplicatedEventApplier] wraps whatever it is handed in `memoizing()`, and that
     * wrapper is idempotent, so handing out a *new* plain resolver each call downgraded every attestation to a
     * per-call memo. And even one memo per batch does not help by itself: the codec puts at most one commitment in a
     * batch, so there is nothing to share within it. A commitment's Link is its whole ancestry, so a batch starting
     * from an empty memo rebuilt the entire chain below its one commitment — quadratic over a chain of batches.
     *
     * Reading the base store's memo is what makes it linear: by the time this batch is validated, the previous
     * commitment's Link is already memoized there (the accepted apply resolved it), so the ancestry is a hit and only
     * the new head is built. Writes deliberately do not go back: this overlay resolves records the batch merely
     * *proposes*, and a batch that fails validation is discarded whole, so its resolutions must die with it. See
     * [memoizingOver] for why one-way sharing is sound and why it needs a short scope — one batch is short enough,
     * as the base store cannot narrow while a validation is in flight.
     */
    private val memo = ProjectedLinkRecordResolver { addedPreimages[it] ?: baseResolver.recordFor(it) }
        .memoizingOver(baseResolver)

    override fun resolver(): ProjectedLinkRecordResolver = memo

    /**
     * What this batch adds, in the order a store must take it: preimages, then attestations, then seals.
     *
     * Sound to commit without re-judging because every input the judgement reads is forwarded straight to [base] — an
     * attestation's `containsAttestation` and the records its `resolver()` walks — so the verdict cannot differ between
     * here and the real store, which has not moved in between (validation and commit are one synchronous step). What is
     * *not* forwarded is the appending: these appends maintain none of the real store's indexes, which is precisely why
     * the caller must re-append rather than adopt this overlay.
     */
    fun accepted(): List<ReplicatedEvent> =
        addedPreimages.values.map(::LinkPreimage) + addedAttestations.values + addedSeals.values

    /** Links resolved during this validation — facts once [accepted] is committed (see [BatchValidationResult.Valid]). */
    fun resolutions(): Map<LinkProjection, Link> = memo.resolutions

    override fun containsPreimage(projection: LinkProjection): Boolean =
        projection in addedPreimages || base.resolver().recordFor(projection) != null

    override fun containsAttestation(head: LinkProjection): Boolean =
        head in addedAttestations || base.containsAttestation(head)

    override fun containsSealed(ciphertextDigest: ByteString): Boolean =
        addedSeals.keys.any { it.first == ciphertextDigest }

    override fun attestation(head: LinkProjection): SignedCommitment? = addedAttestations[head]

    override fun headsOf(credential: LinkProjection): Set<LinkProjection> = emptySet()

    override fun frontier(): Map<LinkProjection, Set<LinkProjection>> = emptyMap()

    /** An overlay only ever proposes; it is discarded whole rather than narrowed, and is never exported from. */
    override val erasures: Int get() = 0

    override fun preimages(): Sequence<ProjectedLinkRecord> = addedPreimages.values.asSequence()

    override fun attestations(): Sequence<SignedCommitment> = addedAttestations.values.asSequence()

    override fun sealedContents(): Sequence<SealedContent> = addedSeals.values.asSequence()

    override fun appendPreimage(record: ProjectedLinkRecord): Boolean {
        if (containsPreimage(record.projection)) return false
        addedPreimages[record.projection] = record
        return true
    }

    override fun appendAttestation(attestation: SignedCommitment): Boolean {
        if (containsAttestation(attestation.head)) return false
        addedAttestations[attestation.head] = attestation
        return true
    }

    override fun appendSealed(sealed: SealedContent): Boolean {
        val key = sealed.ciphertextDigest to sealed.contentProjection
        if (key in addedSeals || base.containsSealed(sealed.contentProjection, sealed.ciphertextDigest))
            return false
        addedSeals[key] = sealed
        return true
    }

    override fun removePreimage(projection: LinkProjection): Boolean = false

    override fun removeSealed(ciphertextDigest: ByteString): Boolean = false
}
